<template>
    <div class="h-full w-full flex flex-col">
        <!-- Header with Institution Filter -->
        <div class="mb-6">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 class="text-2xl font-bold text-gray-800">News Calendar</h2>
                <div class="flex items-center gap-4">
                    <div class="flex items-center gap-2">
                        <label class="text-sm font-medium text-gray-700">Entity:</label>
                        <select v-model="selectedInstitution"
                            class="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
                            <option value="">All</option>
                            <option value="ADPOLY">ADPOLY</option>
                            <option value="FCHS">FCHS</option>
                            <option value="ATS">ATS</option>
                            <option value="ADVETI">ADVETI</option>
                        </select>
                    </div>
                    <div class="flex items-center gap-2">
                        <label class="text-sm font-medium text-gray-700">View:</label>
                        <div class="flex border border-gray-300 rounded-lg overflow-hidden">
                            <button @click="viewMode = 'list'"
                                :class="viewMode === 'list' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                class="px-4 py-2 text-sm font-medium transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-list" class="mr-2"></font-awesome-icon>
                                List
                            </button>
                            <button @click="viewMode = 'calendar'"
                                :class="viewMode === 'calendar' ? 'bg-primary text-white' : 'bg-white text-gray-700 hover:bg-gray-50'"
                                class="px-4 py-2 text-sm font-medium transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-calendar-days" class="mr-2"></font-awesome-icon>
                                Calendar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Category Filter Tabs -->
        <div class="mb-6">
            <div class="flex flex-wrap gap-2">
                <button v-for="category in availableCategories" :key="category" @click="selectedCategory = category"
                    :class="selectedCategory === category ? 'bg-primary text-white' : 'bg-white text-primary border border-primary hover:bg-primary hover:text-white'"
                    class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200">
                    {{ category }}
                </button>
            </div>
        </div>

        <!-- Content Area -->
        <transition name="fade" mode="out-in">
            <div v-if="viewMode === 'list'" key="list" class="flex-1">
                <!-- Show content if there are filtered news -->
                <div v-if="hasFilteredNews" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <!-- News by Category -->
                    <div v-for="category in getFilteredCategories" :key="category"
                        class="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div class="bg-1 text-white p-4">
                            <h3 class="text-xl font-bold">{{ category }}</h3>
                            <p class="text-purple-100 text-sm">Latest News & Updates</p>
                        </div>
                        <div class="p-6">
                            <div class="space-y-3">
                                <news-calendar-card v-for="news in getFilteredNewsByCategory(category)" :key="news.id"
                                    :news="news"></news-calendar-card>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- No Data Message -->
                <div v-else class="flex flex-col items-center justify-center py-16 px-4">
                    <div class="text-center max-w-md">
                        <!-- Icon -->
                        <div class="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gray-100 mb-6">
                            <font-awesome-icon icon="fa-solid fa-newspaper"
                                class="h-10 w-10 text-gray-400"></font-awesome-icon>
                        </div>

                        <!-- Title -->
                        <h3 class="text-xl font-semibold text-gray-900 mb-2">
                            No News Available
                        </h3>

                        <!-- Description -->
                        <p class="text-gray-600 mb-6">
                            <span v-if="selectedInstitution && selectedInstitution !== ''">
                                No news is available for <strong>{{
                                    getInstitutionName(selectedInstitution) }}</strong> at this time.
                            </span>
                            <span v-else>
                                No news is available for the selected filters.
                            </span>
                        </p>

                        <!-- Action Buttons -->
                        <div class="flex flex-col sm:flex-row gap-3 justify-center">
                            <button @click="selectedInstitution = ''"
                                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-eye" class="mr-2"></font-awesome-icon>
                                View All Entities
                            </button>
                            <button @click="selectedCategory = 'All'"
                                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-filter" class="mr-2"></font-awesome-icon>
                                Clear Filters
                            </button>
                        </div>

                        <!-- Additional Info -->
                        <div class="mt-6 p-4 bg-blue-50 rounded-lg">
                            <p class="text-sm text-blue-700">
                                <font-awesome-icon icon="fa-solid fa-info-circle" class="mr-2"></font-awesome-icon>
                                News and updates will be displayed here as they become available.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else key="calendar" class="flex-1">
                <div v-if="hasFilteredNews" class="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div class="p-6">
                        <FullCalendar ref="fullCalendar" class="demo-app-calendar" :options="calendarOptions">
                            <template v-slot:eventContent="arg">
                                <div class="truncate text-xs font-medium">
                                    {{ arg.event.title }}
                                </div>
                            </template>
                        </FullCalendar>
                    </div>
                </div>

                <!-- No Data Message for Calendar View -->
                <div v-else class="flex flex-col items-center justify-center py-16 px-4">
                    <div class="text-center max-w-md">
                        <!-- Icon -->
                        <div class="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gray-100 mb-6">
                            <font-awesome-icon icon="fa-solid fa-newspaper"
                                class="h-10 w-10 text-gray-400"></font-awesome-icon>
                        </div>

                        <!-- Title -->
                        <h3 class="text-xl font-semibold text-gray-900 mb-2">
                            No News Events
                        </h3>

                        <!-- Description -->
                        <p class="text-gray-600 mb-6">
                            <span v-if="selectedInstitution && selectedInstitution !== ''">
                                No news events are available for <strong>{{
                                    getInstitutionName(selectedInstitution)
                                }}</strong> at this time.
                            </span>
                            <span v-else>
                                No news events are available for the selected filters.
                            </span>
                        </p>

                        <!-- Action Buttons -->
                        <div class="flex flex-col sm:flex-row gap-3 justify-center">
                            <button @click="selectedInstitution = ''"
                                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-eye" class="mr-2"></font-awesome-icon>
                                View All Entities
                            </button>
                            <button @click="selectedCategory = 'All'"
                                class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200">
                                <font-awesome-icon icon="fa-solid fa-filter" class="mr-2"></font-awesome-icon>
                                Clear Filters
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import NewsCalendarCard from '../../components/events/NewsCalendarCard.vue'

export default {
    name: "news-calendar",
    components: {
        FullCalendar,
        NewsCalendarCard,
    },
    data() {
        return {
            selectedInstitution: '',
            viewMode: 'list',
            selectedCategory: 'All',
            calendarOptions: {
                plugins: [
                    dayGridPlugin,
                    timeGridPlugin,
                    interactionPlugin
                ],
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                },
                initialView: 'dayGridMonth',
                editable: false,
                selectable: true,
                selectMirror: true,
                dayMaxEvents: true,
                weekends: true,
                events: [],
                eventDisplay: 'block',
                eventColor: '#8b5cf6',
                eventTextColor: '#ffffff',
                height: 'auto',
            },
            // Static News Calendar Data - Will be replaced with actual data later
            newsEvents: [
                // Sample news events - to be replaced with actual data
                // {
                //     id: 1,
                //     entity: 'ADPOLY',
                //     category: 'Academic Updates',
                //     title: 'New Program Launch',
                //     description: 'Introduction of new academic programs for the upcoming semester',
                //     dateRange: '15 Jan 2025',
                //     startDate: '2025-01-15',
                //     endDate: '2025-01-15',
                //     color: '#8b5cf6',
                //     type: 'announcement'
                // },
                // {
                //     id: 2,
                //     entity: 'ADPOLY',
                //     category: 'Events',
                //     title: 'Career Fair 2025',
                //     description: 'Annual career fair for students and graduates',
                //     dateRange: '20-22 Feb 2025',
                //     startDate: '2025-02-20',
                //     endDate: '2025-02-22',
                //     color: '#8b5cf6',
                //     type: 'event'
                // },
                // {
                //     id: 3,
                //     entity: 'FCHS',
                //     category: 'Research',
                //     title: 'Research Symposium',
                //     description: 'Annual research symposium showcasing student and faculty research',
                //     dateRange: '10 Mar 2025',
                //     startDate: '2025-03-10',
                //     endDate: '2025-03-10',
                //     color: '#8b5cf6',
                //     type: 'conference'
                // }
            ]
        }
    },
    computed: {
        availableCategories() {
            const categories = ['All'];
            const uniqueCategories = [...new Set(this.newsEvents.map(news => news.category))];
            return [...categories, ...uniqueCategories];
        },
        filteredNews() {
            let news = this.newsEvents;

            if (this.selectedInstitution !== '' && this.selectedInstitution !== '') {
                news = news.filter(newsItem => newsItem.entity === this.selectedInstitution);
            }

            if (this.selectedCategory !== 'All') {
                news = news.filter(newsItem => newsItem.category === this.selectedCategory);
            }

            return news;
        },
        hasFilteredNews() {
            return this.filteredNews.length > 0;
        },
        getFilteredCategories() {
            const categories = [...new Set(this.filteredNews.map(news => news.category))];
            return categories;
        }
    },
    watch: {
        filteredNews: {
            handler() {
                this.updateCalendarEvents();
            },
            immediate: true
        },
        selectedCategory: {
            handler(newCategory) {
                if (newCategory !== 'All' && this.viewMode === 'calendar') {
                    this.navigateToCategory(newCategory);
                }
            }
        },
        viewMode: {
            handler(newMode) {
                if (newMode === 'calendar' && this.selectedCategory !== 'All') {
                    // Wait for calendar to be fully rendered
                    this.$nextTick(() => {
                        this.waitForCalendarAndNavigate();
                    });
                }
            }
        }
    },
    methods: {
        getFilteredNewsByCategory(category) {
            return this.filteredNews.filter(news => news.category === category);
        },
        updateCalendarEvents() {
            const calendarEvents = this.filteredNews.map(news => ({
                id: news.id,
                title: news.title,
                start: news.startDate,
                end: news.endDate,
                backgroundColor: news.color,
                borderColor: news.color,
                textColor: '#ffffff',
                extendedProps: {
                    description: news.description,
                    entity: news.entity,
                    category: news.category,
                    type: news.type
                }
            }));

            this.calendarOptions.events = calendarEvents;
        },
        getInstitutionName(entity) {
            const institutionNames = {
                'ADPOLY': 'Abu Dhabi Polytechnic',
                'FCHS': 'Fatima College of Health Sciences',
                'ADVETI': 'Abu Dhabi Vocational Education and Training Institute',
                'ATS': 'Applied Technology Schools'
            };
            return institutionNames[entity] || entity;
        },
        waitForCalendarAndNavigate() {
            let attempts = 0;
            const maxAttempts = 10;

            const checkCalendar = () => {
                attempts++;
                if (this.$refs.fullCalendar && this.$refs.fullCalendar.getApi) {
                    try {
                        const api = this.$refs.fullCalendar.getApi();
                        if (api && api.getCurrentData) {
                            // Calendar is ready, navigate to category
                            this.navigateToCategory(this.selectedCategory);
                            return;
                        }
                    } catch (error) {
                        // Calendar not ready yet
                    }
                }

                if (attempts < maxAttempts) {
                    setTimeout(checkCalendar, 100);
                }
            };

            checkCalendar();
        },
        navigateToCategory(category) {
            if (this.$refs.fullCalendar) {
                try {
                    const calendarApi = this.$refs.fullCalendar.getApi();

                    // Navigate to the earliest news of the category for better user experience
                    const categoryNews = this.newsEvents.filter(news => news.category === category);
                    if (categoryNews.length > 0) {
                        // Find the earliest news start date
                        const earliestNews = categoryNews.reduce((earliest, current) => {
                            return new Date(current.startDate) < new Date(earliest.startDate) ? current : earliest;
                        });

                        // Navigate to the month containing the earliest news
                        const earliestMonth = earliestNews.startDate.substring(0, 7); // YYYY-MM
                        calendarApi.gotoDate(earliestMonth + '-01');
                    }

                    // Ensure the calendar is properly rendered
                    setTimeout(() => {
                        calendarApi.render();
                    }, 100);
                } catch (error) {
                    console.log('Error navigating to category:', error);
                    // Fallback: try again after a longer delay
                    setTimeout(() => {
                        this.navigateToCategory(category);
                    }, 1000);
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.bg-1 {
    background: linear-gradient(90deg, rgba(34, 42, 57, 1) 0%, rgba(10, 66, 117, 1) 100%);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

:deep(.fc) {
    .fc-event {
        border-radius: 4px;
        font-size: 0.75rem;
        padding: 2px 4px;
    }

    .fc-daygrid-event {
        margin: 1px 0;
    }

    .fc-event-title {
        font-weight: 500;
    }

    .fc-toolbar-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: #374151;
    }

    .fc-button {
        background-color: #8b5cf6;
        border-color: #8b5cf6;
        font-weight: 500;

        &:hover {
            background-color: #7c3aed;
            border-color: #7c3aed;
        }

        &.fc-button-active {
            background-color: #6d28d9;
            border-color: #6d28d9;
        }
    }

    .fc-daygrid-day-number {
        color: #374151;
        font-weight: 500;
    }

    .fc-col-header-cell {
        background-color: #f9fafb;
        color: #374151;
        font-weight: 600;
    }
}
</style>
